/* eslint-disable max-len */
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import GradesAndPromotions_Layout from '../../../../components/FormLayouts/GradesAndPromotions/GradesAndPromotionsLayout';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import { useAppSelector } from '../../../../redux/hooks';
import {
  clearGradesAndPromotions,
  saveGradesAndPromotions,
  selectGradesAndPromotionsDataEntryMode,
  selectGradesAndPromotionsFormViewModel,
  selectGradesAndPromotionsGradesAndPromotions,
  selectGradesAndPromotionsLoadingFormViewModel,
  selectGradesAndPromotionsLoadingGradesAndPromotions,
  selectSchoolYear,
  setGradesAndPromotionsDataEntryMode,
} from '../../../../redux/slices/gradesAndPromotionsSlice';
import {
  fetchGradesAndPromotionsById,
  fetchGradesAndPromotionsFormViewModel,
} from '../../../../redux/thunks/gradesAndPromotionsThunks';
import { getCurrentUserAccess } from '../../../../services/base.service';
import {
  createGradesAndPromotions,
  updateGradesAndPromotions,
} from '../../../../services/gradesAndPromotions.service';
import { appRoutePaths } from '../../../../services/route.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { GradesAndPromotionsFormViewModel } from '../../../../types/GradesAndPromotionsFormViewModel';
import { GradesAndPromotionsModel } from '../../../../types/GradesAndPromotionsModel';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { GradesAndPromotionsValidationSchema } from '../../../../types/Validation/GradesAndPromotionsValidationSchema';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import { formatDistrictForDisplay } from '../../../../utilities/textUtilities';

interface DirtyStateContextType {
  userHasMadeChanges: boolean;
  setUserHasMadeChanges: (value: boolean) => void;
  isDataLoading: boolean;
  setIsDataLoading: (value: boolean) => void;
}

const DirtyStateContext = createContext<DirtyStateContextType | undefined>(
  undefined
);

export const useDirtyState = (): DirtyStateContextType => {
  const context = useContext(DirtyStateContext);
  if (!context) {
    throw new Error('useDirtyState must be used within a DirtyStateProvider');
  }
  return context;
};

const GradesAndPromotionsInput = (): ReactElement => {
  const model: GradesAndPromotionsModel = useAppSelector(
    selectGradesAndPromotionsGradesAndPromotions
  );
  const formViewModel: GradesAndPromotionsFormViewModel = useAppSelector(
    selectGradesAndPromotionsFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectGradesAndPromotionsLoadingFormViewModel
  );
  const isLoadingModel = useAppSelector(
    selectGradesAndPromotionsLoadingGradesAndPromotions
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectGradesAndPromotionsDataEntryMode
  );
  const schoolYear = useAppSelector(selectSchoolYear);
  const isLoading = isLoadingModel || isLoadingFormViewModel;

  const [userHasMadeChanges, setUserHasMadeChanges] = useState<boolean>(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [previousModelId, setPreviousModelId] = useState<string | undefined>(
    model.id
  );

  useEffect(() => {
    if (model.id !== previousModelId) {
      setUserHasMadeChanges(false);
      setIsDataLoading(true);
      setPreviousModelId(model.id);
    }
  }, [model.id, previousModelId]);

  useEffect(() => {
    if (isDataLoading && !isLoading) {
      setIsDataLoading(false);
    }
  }, [isDataLoading, isLoading]);

  const getDistrictDisplay = (): string => {
    if (dataEntryMode === DataEntryMode.EDIT) {
      return model.districtName;
    } else {
      const user = getCurrentUserAccess();
      return formatDistrictForDisplay(user?.currentDistrict);
    }
  };

  const getSchoolYear = (): string => {
    // If redux is filled use that, otherwise use the default from the form model
    if (schoolYear !== '') {
      return schoolYear;
    } else {
      return formViewModel.defaultSchoolYear;
    }
  };

  const getSchoolDisplay = (): string => {
    return dataEntryMode === DataEntryMode.EDIT ? `${model.schoolName}` : '';
  };

  const intitalValues: GradesAndPromotionsModel = {
    ...model,
    districtName: getDistrictDisplay(),
    schoolYear: getSchoolYear(),
    schoolName: getSchoolDisplay(),
  };

  const customDirtyStateHandler = (dirty: boolean, hasAnyFieldBeenTouched?: boolean): boolean => {
    return dirty && !!hasAnyFieldBeenTouched && !isDataLoading;
  };

  return (
    <DirtyStateContext.Provider
      value={{
        userHasMadeChanges,
        setUserHasMadeChanges,
        isDataLoading,
        setIsDataLoading,
      }}
    >
      <ManualInputPage<
        GradesAndPromotionsModel,
        GradesAndPromotionsFormViewModel
      >
        actions={ManualInputCrudAction.CREATE_AND_EDIT}
        loadingOptions={{
          isLoading: isLoading,
          loadingDataId: 'grades-and-promotions-input-loader',
        }}
        navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
        classes={{
          pageClass: 'data-input--grades-and-promotions--input',
        }}
        parameterOptions={{
          routeParameterName: 'schoolYear',
          identityName: model.id,
        }}
        routes={{
          backBarRoute: appRoutePaths.GradesAndPromotionsList,
          cancelRoute: appRoutePaths.GradesAndPromotionsList,
          createSuccessRoute: appRoutePaths.GradesAndPromotionsInput,
        }}
        dataFunctions={{
          loadDataDetailById: fetchGradesAndPromotionsById,
          loadFormDetail: fetchGradesAndPromotionsFormViewModel,
          setEntryMode: setGradesAndPromotionsDataEntryMode,
          setData: saveGradesAndPromotions,
          clearData: clearGradesAndPromotions,
          onCreate: createGradesAndPromotions,
          onUpdate: updateGradesAndPromotions,
        }}
        formDetails={{
          validationSchema: GradesAndPromotionsValidationSchema,
          initialFormValues: intitalValues,
          isFormModelEmpty: true,
        }}
        config={{
          dataEntryMode: dataEntryMode,
          objectVerbiage: 'Update Grades and Promotions',
          subHeading: 'Update Grades and Promotions',
          pageHeader: 'Student',
        }}
        DEV_MODE={GLOBAL_DEBUG_MODE}
        REDIRECT_TO_LIST_PAGE={true}
        customDirtyStateHandler={customDirtyStateHandler}
      >
        <GradesAndPromotions_Layout />
      </ManualInputPage>
    </DirtyStateContext.Provider>
  );
};

export default GradesAndPromotionsInput;
